"use client";

import { useEffect, useState } from "react";

import type CodeBlockComponent from "./CodeBlock";
import {
  codeBlockLanguageLabel,
  codeBlockMaxHeight,
  codeBlockWrapperClassName,
  normalizeCodeValue,
  type CodeBlockProps,
} from "./code-block-layout";

/**
 * Renders the code as plain text on the server, then swaps in the Prism-highlighted CodeBlock on the client.
 *
 * The code text must be in the initial HTML, because crawlers and AI fetchers do not execute JavaScript.
 * CodeBlock is loaded through a plain import() in an effect, not next/dynamic or a static import, so Prism
 * and its language grammars stay out of the initial bundle and are only fetched by pages that render code.
 */
export default function LazyCodeBlock(props: CodeBlockProps) {
  const [CodeBlock, setCodeBlock] = useState<typeof CodeBlockComponent | null>(null);

  useEffect(() => {
    let cancelled = false;
    import("./CodeBlock").then((module) => {
      if (!cancelled) {
        setCodeBlock(() => module.default);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (CodeBlock) {
    return <CodeBlock {...props} />;
  }

  const { code, filename, language } = normalizeCodeValue(props);

  return (
    <div id="code-snippet" className={codeBlockWrapperClassName(props.fullSize, props.className)}>
      {!!language && (
        <div className="flex pl-4 pr-2 py-1.5 border-b border-b-grey-850 tracking-wide text-sm">
          <span className="text-red text-xs leading-7">{codeBlockLanguageLabel(language)}</span>
        </div>
      )}
      <pre
        className="bg-transparent! relative py-2 my-0! pl-[37px] pr-4 overflow-x-auto text-xs leading-relaxed"
        style={{ maxHeight: codeBlockMaxHeight(props.maxHeight) }}
      >
        {filename && (
          <span className="block mt-2 mb-4 -ml-[21px] w-fit font-mono text-xxs font-semibold tracking-wider bg-grey-850 rounded-sm p-2 leading-none h-6">
            {filename}
          </span>
        )}
        <code>{code.replace(/\n+$/, "")}</code>
      </pre>
      {props.footer && <div className="border-t border-grey-850 px-4 py-3 text-sm">{props.footer}</div>}
    </div>
  );
}
